import React, { useEffect, useMemo, useRef, useState } from "react";
import InputIcon from "../assets/images/inputIcon.png";
import SearchableSelect from "./SearchableSelect";
import Swal from "sweetalert2";
import { KSA_CITIES } from "../utils/KSACities";

const COUNTRY_CODE = "SA";
const COUNTRY_NAME = "السعودية";

// Normalize Arabic for client-side comparisons (ا ~ أ/إ/آ)
const normalizeAr = (s) =>
  (s ?? "")
    .toString()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[أإآ]/g, "ا");

// Build an Overpass regex that is tolerant to hamza forms and spacing
const buildOverpassNameRegex = (raw) => {
  const safe = String(raw || "")
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // escape regex metachars
    .replace(/ا/g, "[اأإآ]")                 // lenient alef forms
    .replace(/\s+/g, "\\s*");               // flexible spacing
  return safe;
};

function CartLocationSelectors({ value, onChange }) {
  // form state
  const [cityId, setCityId] = useState(value?.cityId || "");
  const [hood, setHood] = useState(value?.hood || "");
  const [street, setStreet] = useState(value?.street || "");
  const [fullName, setFullName] = useState(value?.fullName || "");
  const [phone, setPhone] = useState(value?.phone || "");

  // data state
  const [cities, setCities] = useState([]);
  const [hoods, setHoods] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingHoods, setLoadingHoods] = useState(false);
  const [error, setError] = useState("");

  // refs (for focusing after validation)
  const fullNameRef = useRef(null);
  const phoneRef = useRef(null);
  const cityWrapRef = useRef(null);
  const hoodWrapRef = useRef(null);

  // stores center for custom (free-typed) cities
  const [customCityCenter, setCustomCityCenter] = useState(null);

  // ---------- Validation helpers ----------
  const isValidFullName = (name) => {
    const parts = String(name).trim().replace(/\s+/g, " ").split(" ").filter(Boolean);
    return parts.length >= 2; // first + last (or more)
  };

  const swalError = async (title, text, focusEl) => {
    await Swal.fire({
      icon: "error",
      title,
      text,
      confirmButtonText: "حسناً",
      confirmButtonColor: "#CAEA3E",
      iconColor: "#CAEA3E",
      customClass: { popup: "swal-dark-popup", cancelButton: "swal-cancel-btn" },
    });
    if (focusEl?.current) focusEl.current.focus();
    else if (focusEl instanceof HTMLElement) focusEl.focus();
  };

  const handleFullNameChange = (e) => setFullName(e.target.value.replace(/\s{2,}/g, " "));
  const handleFullNameBlur = async () => {
    // if (!fullName.trim()) {
    //   await swalError("حقل مطلوب", "رجاءً أدخل اسم المستلم.", fullNameRef);
    //   return;
    // }
    // if (!isValidFullName(fullName)) {
    //   await swalError(
    //     "الاسم غير مكتمل",
    //     "رجاءً أدخل الاسم الأول ثم اسم العائلة (على الأقل). يمكنك إضافة اسم الأب أيضاً.",
    //     fullNameRef
    //   );
    // }
  };
  const handleFullNameKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await handleFullNameBlur();
    }
  };

  const handlePhoneBlur = async () => {
    // if (!phone.trim()) {
    //   await swalError("حقل مطلوب", "رجاءً أدخل رقم الجوال.", phoneRef);
    //   return;
    // }
    // Add your exact SA phone validation here if you want to enforce format
    // if (!/^05\d{8}$/.test(phone.trim())) {
    //   await swalError("رقم غير صالح", "أدخل رقم سعودي بصيغة 05xxxxxxxx.", phoneRef);
    // }
  };

  const requireCityIfEmpty = async () => {
    if (!String(cityId || "").trim()) {
      const input = cityWrapRef.current?.querySelector("input");
      await swalError("حقل مطلوب", "رجاءً اختر المدينة.", input || cityWrapRef.current);
      return false;
    }
    return true;
  };
  const requireHoodIfEmpty = async () => {
    if (!String(hood || "").trim()) {
      const input = hoodWrapRef.current?.querySelector("input");
      await swalError("حقل مطلوب", "رجاءً اختر الحي.", input || hoodWrapRef.current);
      return false;
    }
    return true;
  };
  // ---------------------------------------

  // keep in sync if parent updates
  useEffect(() => {
    if (value?.fullName !== undefined) setFullName(value.fullName || "");
    if (value?.phone !== undefined) setPhone(value.phone || "");
    if (value?.street !== undefined) setStreet(value.street || "");
  }, [value?.fullName, value?.phone, value?.street]);

  const selectedCityObj = useMemo(
    () => cities.find((c) => String(c.id) === String(cityId)),
    [cities, cityId]
  );

  const isCityInList = useMemo(
    () => cities.some((c) => String(c.id) === String(cityId)),
    [cities, cityId]
  );

  // Geocode custom city name (when cityId is a free text, not one of the known ids)
  useEffect(() => {
    // If empty or chosen from list -> clear custom center & exit
    if (!cityId || isCityInList) {
      setCustomCityCenter(null);
      return;
    }

    const name = String(cityId).trim();
    if (!name) {
      setCustomCityCenter(null);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        // Immediately show loading for hoods (as requested)
        setLoadingHoods(true);
        // Clear previous hoods while searching a new city
        setHoods([]);
        setHood("");

        const pattern = buildOverpassNameRegex(name);
        const q = `
[out:json][timeout:30];
area["ISO3166-1"="${COUNTRY_CODE}"][admin_level=2];
(
  node["place"~"^(city|town)$"]["name"~"${pattern}",i](area);
  way["place"~"^(city|town)$"]["name"~"${pattern}",i](area);
  relation["place"~"^(city|town)$"]["name"~"${pattern}",i](area);
);
out tags center 1;`;

        const res = await fetch("https://overpass-api.de/api/interpreter", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
          body: "data=" + encodeURIComponent(q),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const el = (data.elements || [])[0];
        

        if (el?.center || (el?.lat && el?.lon)) {
          const center = el.center ?? { lat: el.lat, lon: el.lon };
          setCustomCityCenter(center); // will trigger the hoods fetch effect below
        } else {
          // No center found -> no hoods (only "اخرى" will be shown)
          setCustomCityCenter(null);
          setLoadingHoods(false);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          // Fail gracefully
          setCustomCityCenter(null);
          setLoadingHoods(false);
        }
      }
    })();

    return () => controller.abort();
  }, [cityId, isCityInList]);

  // bubble up to parent
  useEffect(() => {
    onChange?.({
      country: COUNTRY_NAME,
      cityId, // can be option id OR custom string
      cityName: selectedCityObj?.name || (selectedCityObj ? "" : String(cityId || "")),
      hood,
      street,
      fullName,
      phone,
    });
  }, [cityId, selectedCityObj, hood, street, fullName, phone, onChange]);

  // fetch cities
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoadingCities(true);
      setError("");
      const q = `
[out:json][timeout:30];
area["ISO3166-1"="${COUNTRY_CODE}"][admin_level=2];
(
  node["place"~"city|town"](area);
  way["place"~"city|town"](area);
  relation["place"~"city|town"](area);
);
out tags center;`;
      try {
        // const res = await fetch("https://overpass-api.de/api/interpreter", {
        //   method: "POST",
        //   headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        //   body: "data=" + encodeURIComponent(q),
        //   signal: controller.signal,
        // });
        // if (!res.ok) throw new Error(String(res.status));
        // const data = await res.json();
        // const list = (data.elements || [])
        //   .map((el) => ({
        //     id: el.id,
        //     type: el.type,
        //     name: el.tags?.name || "",
        //     center: el.center || (el.lat && el.lon ? { lat: el.lat, lon: el.lon } : null),
        //   }))
        //   .filter((c) => c.name && c.center)
        //   .sort((a, b) => normalizeAr(a.name).localeCompare(normalizeAr(b.name), "ar"));
        //   console.log(list);
          
        setCities(KSA_CITIES);
        if (!value?.cityId) {
          setCityId("");
          setHoods([]);
          setHood("");
        }
      } catch (e) {
        if (e.name !== "AbortError") setError("تعذّر جلب المدن حالياً.");
      } finally {
        setLoadingCities(false);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch hoods (uses either selected city center or custom city center)
  // fetch hoods (uses either selected city center or custom city center)
  const selectedCenter = selectedCityObj?.center || customCityCenter;

  useEffect(() => {
    if (!selectedCenter) {
      setHoods([]);
      setLoadingHoods(false);
      // keep the user's typed hood; or clear if you prefer:
      // setHood("");
      return;
    }

    const controller = new AbortController();
    (async () => {
      setLoadingHoods(true);
      setError("");
      const { lat, lon } = selectedCenter;
      const RADIUS_M = 30000;
      const q = `
[out:json][timeout:30];
(
  node["place"~"^(neighbourhood|suburb|quarter|district|city_district|borough|village|hamlet)$"](around:${RADIUS_M},${lat},${lon});
  way["place"~"^(neighbourhood|suburb|quarter|district|city_district|borough|village|hamlet)$"](around:${RADIUS_M},${lat},${lon});
  relation["place"~"^(neighbourhood|suburb|quarter|district|city_district|borough|village|hamlet)$"](around:${RADIUS_M},${lat},${lon});
);
out tags center;`;

      try {
        const res = await fetch("https://overpass-api.de/api/interpreter", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
          body: "data=" + encodeURIComponent(q),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();

      const norm = (s) => s?.toString().toLowerCase().replace(/[أإآ]/g, "ا") ?? "";
      const names = Array.from(new Set((data.elements || [])
        .map((el) => el.tags?.name)
        .filter(Boolean)))
        .sort((a, b) => norm(a).localeCompare(norm(b), "ar"));

      setHoods(names);
      if (names.length && !names.includes(hood)) setHood("");
    } catch (e) {
      if (e.name !== "AbortError") setError("تعذّر جلب الأحياء حالياً.");
    } finally {
      setLoadingHoods(false);
    }
  })();

    return () => controller.abort();
  }, [selectedCenter]); // ✅ ONLY selectedCenter here


  // build options for cities/hoods
  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: String(c.id), label: c.name })),
    [cities]
  );
  const hoodOptions = useMemo(
    () => hoods.map((h) => ({ value: h, label: h })),
    [hoods]
  );

  // If the city value is a custom string (not in list), bump the hood selector enabled state
  const cityChosen = Boolean(cityId);

  return (
    <div className="address-box" dir="rtl">
      {/* Row 1: المدينة | الحي | الشارع */}
      <div className="addr-grid">
        <div className="addr-field" ref={cityWrapRef}>
          <span className="addr-label">المدينة *</span>
          <SearchableSelect
            options={cityOptions}
            value={String(cityId || "")}
            onChange={(v) => {
              const isKnown = cities.some((c) => String(c.id) === String(v));
              if (v && !isKnown) {
                // user typed a custom city → we'll geocode → show loader
                setLoadingHoods(true);
                setCustomCityCenter(null);
                setHoods([]);
                setHood("");
              } else if (!v) {
                // user cleared the input → stop loader and clear data
                setLoadingHoods(false);
                setCustomCityCenter(null);
                setHoods([]);
                setHood("");
              }
              setCityId(v);
            }}
            onBlur={requireCityIfEmpty}
            placeholder="ابحث عن المدينة"
            disabled={loadingCities}
            loading={loadingCities}
            dataLabel="المدينة"
            name="city"
          />

        </div>

        {/* الحي */}
        <div className="addr-field" ref={hoodWrapRef}>
          <span className="addr-label">الحي *</span>
          <SearchableSelect
            options={hoodOptions}            // if empty → component will still show "اخرى" first
            value={hood}
            onChange={(v) => setHood(v)}
            onBlur={requireHoodIfEmpty}
            placeholder={cityChosen ? "ابحث عن الحي" : "اختر المدينة أولاً"}
            disabled={!cityChosen || loadingHoods}
            loading={loadingHoods}
            dir="rtl"
            className="addr-ss"
            dataLabel="الحي"
            name="hood"
          />
        </div>

        {/* الشارع */}
        <div className="addr-field">
          <span className="addr-label">الشارع</span>
          <div className="addr-input-wrap">
            <input
              type="text"
              className="addr-input with-ico"
              placeholder="اكتب اسم الشارع"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
            <img src={InputIcon} alt="" className="addr-ico" style={{ width: "30px" }} />
          </div>
        </div>
      </div>

      {/* Row 2: اسم المستلم | رقم جوال المستلم */}
      <div className="addr-row addr-contact" style={{ marginTop: 12 }}>
        <div className="addr-field">
          <span className="addr-label">اسم المستلم *</span>
          <div className="addr-input-wrap">
            <input
              ref={fullNameRef}
              type="text"
              className="addr-input with-ico"
              placeholder="محمد عبد الله"
              value={fullName}
              onChange={handleFullNameChange}
              onBlur={handleFullNameBlur}
              onKeyDown={handleFullNameKeyDown}
              name="fullName"
              required
            />
            <img src={InputIcon} alt="" className="addr-ico" style={{ width: "30px" }} />
          </div>
        </div>

        <div className="addr-field">
          <span className="addr-label">رقم جوال المستلم *</span>
          <div className="addr-input-wrap">
            <input
              ref={phoneRef}
              type="tel"
              inputMode="tel"
              dir="rtl"
              className="addr-input with-ico"
              placeholder="05xxxxxxxx"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={handlePhoneBlur}
              required
            />
            <img src={InputIcon} alt="" className="addr-ico" style={{ width: "30px" }} />
          </div>
        </div>
      </div>

      {error && <div className="addr-error">{error}</div>}
    </div>
  );
}

export default CartLocationSelectors;
